// Utility function to convert string dates to Date objects
const ensureDate = (date: Date | string): Date => {
  if (typeof date === 'string') {
    return new Date(date)
  }
  return date
}

export const getDateFormatted = (date: Date | string): string => ensureDate(date).toLocaleDateString('en-US')

export function getTimeFormatted(date: Date | string): string {
  const dateObj = ensureDate(date)
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return ''

  const hours = String(dateObj.getHours()).padStart(2, '0')
  const minutes = String(dateObj.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

export function getTimeFormatted12Hours(date: Date | string): string {
  const dateObj = ensureDate(date)
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return ''

  let hours = dateObj.getHours()
  const minutes = dateObj.getMinutes()
  const isPM = hours >= 12

  hours = hours % 12 || 12

  const minutesStr = String(minutes).padStart(2, '0')

  return `${hours}:${minutesStr} ${isPM ? 'PM' : 'AM'}`
}

export function getDateFormattedForPicker(date: Date | string): string {
  const dateObj = ensureDate(date)
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return ''

  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0') // months are 0-based
  const day = String(dateObj.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export function getDateFormattedHuman(date: Date | string, isLongForm: boolean = false): string {
  const dateObj = ensureDate(date)
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
    return ''
  }

  const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const longMonths = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const year = dateObj.getFullYear()
  const monthIndex = dateObj.getMonth()
  const day = String(dateObj.getDate()).padStart(2, '0') // "01", "26", etc.

  const monthName = isLongForm ? longMonths[monthIndex] : shortMonths[monthIndex]

  return `${monthName} ${day}, ${year}`
}

export const getDateRangeFormatted = (
  dateStart: Date | string,
  dateEnd: Date | string,
  isLongForm: boolean = true
): string => {
  const dateStartObj = ensureDate(dateStart)
  const dateEndObj = ensureDate(dateEnd)
  if (dateStartObj.getTime() === dateEndObj.getTime()) return getDateFormattedHuman(dateStartObj, isLongForm)
  return `${getDateFormattedHuman(dateStartObj, isLongForm)} - ${getDateFormattedHuman(dateEndObj, isLongForm)}`
}

export const getTimeRangeFormatted = (dateStart: Date | string, dateEnd: Date | string): string => {
  if (getTimeFormatted12Hours(dateStart) === getTimeFormatted12Hours(dateEnd)) {
    return getTimeFormatted12Hours(dateStart)
  }

  return `${getTimeFormatted12Hours(dateStart)} - ${getTimeFormatted12Hours(dateEnd)}`
}

export const isZeroDate = (date: Date | string): boolean => ensureDate(date).getFullYear() > 2000
